<template>
    <div>
        <!-- Elfsight div'leri -->
        <div v-for="appId in elfsightApps" :key="appId" :class="`elfsight-app-${appId}`" data-elfsight-app-lazy />

        <div id="widget-container"></div>

        <!-- FAB Buton -->
        <div class="fab-menu-container" v-if="isWidgetsReady">
            <button @click="toggleWidgets" class="fab-btn" :class="{ active: isWidgetsVisible }">
                <img :src="whiteLogo" alt="Divissa Logo" class="fab-logo" width="26" height="26" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { useRuntimeConfig } from '#imports';
import whiteLogo from '~/assets/images/logo-icon.svg';
declare global {
    interface Window {
        ElfsightApp?: {
            init?: () => void;
        };
    }
}

interface Widget {
    id: string;
    src: string;
    async: boolean;
}

const config = useRuntimeConfig();
const elfsightApps = config.public.elfsightApps;
const widgets = config.public.widgets as Widget[];

const isWidgetsVisible = ref(false);
const isWidgetsReady = ref(false);

const toggleWidgets = () => {
    if (!isWidgetsReady.value) return;
    isWidgetsVisible.value = !isWidgetsVisible.value;

    const widgetElements = document.querySelectorAll('[id^="__EAAPS_PORTAL"], #fc_frame');
    widgetElements.forEach((el, index) => {
        const element = el as HTMLElement;
        if (isWidgetsVisible.value) {
            element.style.display = 'block';
            element.style.opacity = '1';
            element.style.pointerEvents = 'auto';
        } else {
            element.style.display = 'none';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
        }

        element.style.right = '20px';
        element.style.bottom = `${100 + index * 80}px`;
        element.style.transition = 'none'; // Tüm transition'ları kaldır
    });
};


const handleClickOutside = (event: MouseEvent) => {
    const fabContainer = document.querySelector('.fab-menu-container');
    if (fabContainer && !fabContainer.contains(event.target as Node) && isWidgetsVisible.value) {
        isWidgetsVisible.value = false;
        toggleWidgets(); // Hide widgets
    }
};

onMounted(async () => {
    await nextTick();
    if (!process.client) return;

    // Freshchat yükle
    const freshchatScript = document.createElement('script');
    freshchatScript.src = 'https://fw-cdn.com/12233859/4709192.js';
    freshchatScript.setAttribute('chat', 'true');
    freshchatScript.async = false;
    freshchatScript.defer = false;
    document.head.appendChild(freshchatScript);

    const freshchatLoaded = new Promise<void>((resolve) => {
        freshchatScript.onload = () => {
            resolve();
        };
        freshchatScript.onerror = () => resolve();
    });

    // Elfsight yükle
    const elfsightLoaded = widgets.map(widget => {
        return new Promise<void>((resolve) => {
            if (!document.getElementById(widget.id)) {
                const script = document.createElement('script');
                script.id = widget.id;
                script.src = widget.src;
                script.async = widget.async;
                script.onload = () => {
                    resolve();
                };
                script.onerror = () => resolve();
                document.getElementById('widget-container')?.appendChild(script);
            } else {
                resolve();
            }
        });
    });
    await Promise.all([freshchatLoaded, ...elfsightLoaded]);

    // Elfsight init
    if (window.ElfsightApp?.init) {
        window.ElfsightApp.init();
    }

    // Yüklenmiş olsa bile DOM’a giren tüm widget’ları ilk an görünmez yap
    const widgetsToHide = document.querySelectorAll('[id^="__EAAPS_PORTAL"], #fc_frame');
    widgetsToHide.forEach(widget => {
        const el = widget as HTMLElement;
        el.style.display = 'none';
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
        el.style.transition = 'none';
    });

    isWidgetsReady.value = true;


    setTimeout(() => {
        const widgetsToHide = document.querySelectorAll('[id^="__EAAPS_PORTAL"], #fc_frame');
        widgetsToHide.forEach(widget => {
            const el = widget as HTMLElement;
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
            el.style.transition = 'all 0.3s ease';
        });
    }, 1000);

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement && (node.id?.startsWith('__EAAPS_PORTAL') || node.id === 'fc_frame')) {
                    node.style.display = 'none';
                    node.style.opacity = '0';
                    node.style.pointerEvents = 'none';
                    node.style.transition = 'none';
                }
            });
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', handleClickOutside);
});

useHead({
    link: [
        {
            rel: 'preconnect',
            href: 'https://fw-cdn.com',
            crossorigin: 'anonymous'
        }
    ]
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
const route = useRoute();
watch(
    () => route.fullPath,
    () => {
        if (process.client && window.ElfsightApp?.init) {
            window.ElfsightApp.init();
        }
    }
);
</script>
<style lang="less">
.fab-menu-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;

    .fab-btn {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(to right, var(--color-title), var(--color-background-blue));
        border: none;
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        position: relative;
        z-index: 10000;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
            transform: rotate(45deg);
        }

        &:hover {
            transform: scale(1.1);
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);

            .fab-logo {
                transform: rotate(180deg);
            }
        }

        .fab-logo {
            transition: transform 0.5s ease;
        }
    }
}

.widget-wrapper {
    display: inline-block;
}

/* Widget’ları tamamen gizli başlat */
:deep([id^="__EAAPS_PORTAL"]),
:deep(#fc_frame),
:deep(#fc_frame.fc-widget-normal) {
    display: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
    transition: none !important;
    border-radius: 50% !important;
    overflow: hidden !important;
}

#fc_frame,
#fc_frame.fc-widget-normal {
    bottom: 160px !important;
    right: 18px !important;
    z-index: 999999999 !important;
}

/* Trustpilot Button */
.trustpilot-button-wrapper {
    position: fixed;
    bottom: 150px;
    right: 10px;
    z-index: 99999;
    margin: auto 10px auto auto;
    color: white;
    text-decoration: none;

    .trustpilot-button {
        display: inline-flex;
        align-items: center;
        padding: 10px;
        background-color: #000032;
        color: white;
        font-weight: bold;
        border-radius: 5px;
        border: none;
        transition: background-color 0.3s ease;

        &:hover {
            background-color: #000032;
        }

        .trust-pilot-icon {
            margin-right: 10px;
        }
    }
}

:deep(.d-hotline) {
    border-radius: 50% !important;
}

/* Mobil özel stiller */
@media screen and (max-width: 431px) {
    :deep(.d-hotline.h-btn) {
        width: 80px !important;
        height: 80px !important;
    }
}
.ButtonBase__Overlay-sc-11c179a3-4 {
  padding: 18px !important;
}

.ButtonBase__Overlay-sc-11c179a3-4.cjDksG {
  padding: 16px !important;
}

.action-button__CenteredButton-sc-ddd1bfed-0 {
  height: auto !important;
}
.hCawpU {
  bottom: 230px !important;
  margin: 20px 20px 10px 20px !important;
}
.dimPRk {
  left: auto !important;
  right: 0 !important;
  bottom: 75px !important;
  top: auto !important;
}
.EfQos{
  height: 100% !important;
}
.gAGPxT{
  width: 100% !important;
  height: 100% !important;

}
.juGgwj {
  z-index: 100;
}
.kCELwi{
  height: auto !important;
}

</style>